import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Clock, Folder, TrendingUp, Calendar, AlertTriangle, BarChart3, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, CartesianGrid
} from 'recharts';
import { getBackendUrl } from '../utils/getBackendUrl';
import { formatTime } from '../utils/timezone';

const API = `${getBackendUrl()}/api`;

const COLORS = ['#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4', '#f59e0b', '#ec4899', '#14b8a6', '#6366f1'];

const PERIOD_OPTIONS = [
  { label: 'This Week', value: 'week' },
  { label: 'This Month', value: 'month' },
  { label: 'All Time', value: 'all' },
  { label: 'Custom', value: 'custom' },
];

// Renders a small ↑+12 / ↓-3 / – delta tag against a previous-period total
function Delta({ current, previous, label }) {
  if (previous === undefined || previous === null) return null;
  const diff = Math.round((current - previous) * 10) / 10;
  if (Math.abs(diff) < 0.05 && previous === 0 && current === 0) return null;
  let Icon = Minus;
  let color = 'text-gray-500 bg-gray-100';
  if (diff > 0.05) { Icon = ArrowUp; color = 'text-emerald-700 bg-emerald-100'; }
  else if (diff < -0.05) { Icon = ArrowDown; color = 'text-rose-700 bg-rose-100'; }
  const sign = diff > 0 ? '+' : '';
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${color}`} data-testid={`delta-${label}`}>
      <Icon className="w-2.5 h-2.5" />
      {sign}{diff}h vs {label}
    </span>
  );
}

export default function MyAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchData();
  }, [period, startDate, endDate]);

  const getDateRange = () => {
    const now = new Date();
    if (period === 'week') {
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay());
      return { start: start.toISOString().split('T')[0], end: now.toISOString().split('T')[0] };
    }
    if (period === 'month') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: start.toISOString().split('T')[0], end: now.toISOString().split('T')[0] };
    }
    if (period === 'custom' && startDate && endDate) return { start: startDate, end: endDate };
    return {};
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const range = getDateRange();
      const params = new URLSearchParams();
      if (range.start) params.append('start_date', range.start);
      if (range.end) params.append('end_date', range.end);
      const res = await axios.get(`${API}/analytics/my-hours?${params.toString()}`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch personal analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 pt-20 pb-28">
        <div className="max-w-5xl mx-auto animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-56" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[1,2,3,4].map(i => <div key={i} className="h-24 bg-gray-200 rounded-xl" />)}
          </div>
          <div className="h-64 bg-gray-200 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!data || !data.summary) {
    return (
      <div className="p-4 pt-20 pb-28">
        <div className="max-w-5xl mx-auto text-center text-sm text-gray-500">
          Unable to load analytics. Please try refreshing.
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 pt-20 pb-28" data-testid="my-analytics-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-2 mb-1">
          <BarChart3 className="w-6 h-6 text-blue-600" />
          <h1 className="text-xl font-bold text-gray-900">My Analytics</h1>
        </div>
        <p className="text-sm text-gray-500 mb-4">{data.user?.name || 'Your'} personal hours overview</p>

        {/* Period Filter */}
        <div className="flex flex-wrap gap-2 mb-4">
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                period === opt.value ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`my-period-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {period === 'custom' && (
          <div className="flex gap-2 mb-4 items-center">
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm" data-testid="my-custom-start" />
            <span className="text-gray-400">to</span>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm" data-testid="my-custom-end" />
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
            <Clock className="w-5 h-5 text-orange-500 mb-1" />
            <p className="text-2xl font-bold text-gray-900">{data.summary.total_hours}</p>
            <p className="text-xs text-gray-500">Total Hours</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
            <Calendar className="w-5 h-5 text-blue-500 mb-1" />
            <p className="text-2xl font-bold text-gray-900">{data.summary.this_week_hours}</p>
            <p className="text-xs text-gray-500 mb-1">This Week</p>
            <Delta current={data.summary.this_week_hours} previous={data.summary.last_week_hours} label="last wk" />
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
            <Calendar className="w-5 h-5 text-emerald-500 mb-1" />
            <p className="text-2xl font-bold text-gray-900">{data.summary.this_month_hours}</p>
            <p className="text-xs text-gray-500 mb-1">This Month</p>
            <Delta current={data.summary.this_month_hours} previous={data.summary.last_month_hours} label={data.summary.last_month_name || 'last mo'} />
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
            <Folder className="w-5 h-5 text-violet-500 mb-1" />
            <p className="text-2xl font-bold text-gray-900">{data.summary.total_projects}</p>
            <p className="text-xs text-gray-500">Projects Worked</p>
          </div>
        </div>

        {/* Hours by Project */}
        {data.hours_by_project.length > 0 && (
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm mb-4">
            <h2 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
              <Folder className="w-4 h-4 text-green-500" /> Hours by Project
            </h2>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div style={{ width: '100%', maxWidth: '300px', height: 250 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={data.hours_by_project.slice(0, 10)}
                      dataKey="hours"
                      nameKey="name"
                      cx="50%" cy="50%"
                      outerRadius={90}
                      label={({ name, percent }) => `${name.substring(0, 10)}${name.length > 10 ? '...' : ''} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                      fontSize={10}
                    >
                      {data.hours_by_project.slice(0, 10).map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(val) => [`${val} hrs`, 'Hours']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-1 w-full">
                {data.hours_by_project.slice(0, 10).map((p, i) => (
                  <div key={p.name} className="flex items-center gap-2 text-xs">
                    <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="text-gray-700 truncate flex-1">{p.name}</span>
                    <span className="text-gray-500 font-medium">{p.hours} hrs</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Daily Trend */}
        {data.daily_trend.length > 0 && (
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm mb-4">
            <h2 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-purple-500" /> Daily Hours (last 30 days)
            </h2>
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <LineChart data={data.daily_trend} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => { const dt = new Date(d + 'T12:00'); return `${dt.getMonth()+1}/${dt.getDate()}`; }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip labelFormatter={(d) => new Date(d + 'T12:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' })} formatter={(val) => [`${val} hrs`, 'Total Hours']} />
                  <Line type="monotone" dataKey="hours" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Overtime callout */}
        {data.summary.overtime_hours > 0 && (
          <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-800">{data.summary.overtime_weeks} week{data.summary.overtime_weeks !== 1 ? 's' : ''} over 40 hrs</p>
              <p className="text-xs text-red-600">{data.summary.overtime_hours} overtime hours total</p>
            </div>
          </div>
        )}

        {/* Recent Entries Table */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-4 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h2 className="text-sm font-bold text-gray-700 flex items-center gap-2">
              <Clock className="w-4 h-4 text-orange-500" /> Recent Time Entries
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">Last {Math.min(data.recent_entries.length, 50)} entries (Eastern Time)</p>
          </div>

          {data.recent_entries.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">No time entries in this period</div>
          ) : (
            <div className="divide-y divide-gray-100" data-testid="my-entries-list">
              {data.recent_entries.map((e) => (
                <div key={e.id} className="p-3 flex items-start gap-3 hover:bg-gray-50">
                  <div className="flex-shrink-0 w-14 text-center">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase">{new Date(e.date + 'T12:00').toLocaleDateString('en-CA', { month: 'short' })}</p>
                    <p className="text-lg font-bold text-gray-900 leading-tight">{new Date(e.date + 'T12:00').getDate()}</p>
                    <p className="text-[10px] text-gray-400">{new Date(e.date + 'T12:00').toLocaleDateString('en-CA', { weekday: 'short' })}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{e.project_name}</p>
                    <p className="text-xs text-gray-500">
                      {formatTime(e.clock_in)} → {formatTime(e.clock_out)}
                    </p>
                    {e.notes && <p className="text-xs text-gray-400 truncate mt-0.5">{e.notes}</p>}
                    {e.is_rain_day && <span className="inline-block mt-1 text-[10px] font-medium px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">Rain Day</span>}
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className="text-base font-bold text-gray-900">{e.hours}</p>
                    <p className="text-[10px] text-gray-400">hours</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
